import React, { useEffect, useMemo, useState } from 'react';
import { always, and, assoc, compose, cond, defaultTo, find, gt, ifElse, isEmpty, length, map, not, or, prop, propEq, propOr, T, test, update, isNotNil } from 'ramda';
import { isTrue } from 'ramda-adjunct';
import { Badge, Button, Input, Table } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, SlidersOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Components } from '@fasstech/flexiget';
import { Section, Title } from '../../../../components';
import withUser from '../../../../withUser';
import DocumentRules from '../rules/DocumentRules';
import { QGlobalVariablesQuery } from '../../../../_graphql/queries/documents/QGlobalVariables';
import ColorBlock from '../../../elements/colors/components/ColorBlock';
import useColors from '../../../../hooks/useColors';
import defaultColors from '../../../../utils/defaultColors';
import useFetchQuery from '../../../../hooks/useFetchQuery';
import Loading from '../../../../components/Loading';
import { QOfferQuery } from '../../../../_graphql/queries/offers/QOffer';
import colors from '../../../../colors';
import useTag from '../../../../hooks/useTag';
import { SCOPE_OFFER } from '../../../../_CONST';
import GlobalVariableForm from '../globalVariable/GlobalVariableForm';
import TagCurlSection from './TagCurlSection';

const TagItems = ({
  itemType,
  itemsIds = [],
  onItemUp,
  onItemDown,
  rules = [],
  getRulesAreValid,
  getConditionsAreValid,
  variables,
  addTagRule,
  removeTagRule,
  updateTagRule,
  preview,
  userCanEdit,
  setIsOpenCreateGlobalVariableModal,
  tag
}) => {
  itemsIds = compose(
    map(itemId => ({ itemId })),
    defaultTo([])
  )(itemsIds);

  const [displayRules, setDisplayRules] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDisplayRules(itemsIds.map(() => false)), []);

  const addRulesActions = or(!preview, and(preview, length(rules)));

  const dataSource = useMemo(
    () =>
      map((item) => {
        const itemId = prop('itemId', item);
        const rulesItem = compose(
          defaultTo([]),
          prop('rules'),
          find(propEq(itemId, 'itemId'))
        )(rules);
        return {
          itemId,
          rules: rulesItem
        };
      })(defaultTo([])(itemsIds)),
    [itemsIds, rules]
  );

  return (
    <Table
      pagination={false}
      dataSource={dataSource}
      rowKey="itemId"
      locale={{ emptyText: 'Aucune donnée' }}
    >
      {!preview && length(dataSource) > 1 && (
        <Table.Column
          dataIndex="itemId"
          title=""
          width={80}
          render={(itemId, item, index) => {
            return (
              <div className="flex">
                <div className="w-30px">
                  {index > 0 && <ArrowUpOutlined onClick={() => onItemUp(index)} />}
                </div>

                <div className="w-30px">
                  {index < length(dataSource) - 1 && <ArrowDownOutlined onClick={() => onItemDown(index)} />}
                </div>
              </div>
            );
          }}
        />
      )}

      <Table.Column
        dataIndex="itemId"
        title="Nom"
        render={itemId => {
          if (itemType === 'document') {
            const name = propOr(itemId, 'name', find(propEq(itemId, '_id'))(prop('items', tag)));
            return (<span>{name}</span>);
          }

          return <span>{itemId}</span>;
        }}
      />
      {addRulesActions && (
        <Table.Column
          title=""
          render={(item, _, index) => {
            const itemId = prop('itemId', item);
            const rulesItem = prop('rules', item);

            const tooltipTitle = ifElse(
              isTrue,
              ifElse(
                () => gt(length(rules), 1),
                always('Voir le(s) règle(s)'),
                always('Voir la règle')
              ),
              always('Ajouter une règle')
            )(preview);

            return (
              <div className="flex flex-col items-end text-sm">
                <Badge
                  dot={gt(length(rulesItem), 0)}
                  color={gt(length(rulesItem), 0) ? colors['flexi-primary-color'] : undefined}
                >
                  <Components.Tooltip
                    title={tooltipTitle}
                  >
                    <SlidersOutlined
                      className="btn-rules"
                      onClick={() => setDisplayRules(update(index, !displayRules[index]))}
                      rotate={90}
                    />
                  </Components.Tooltip>
                </Badge>
                {displayRules[index] && (
                  <DocumentRules
                    variables={variables}
                    rules={rulesItem}
                    rulesAreValid={getRulesAreValid(rulesItem)}
                    getConditionsAreValid={getConditionsAreValid}
                    onAdd={() => addTagRule(itemId)}
                    onRemove={removeTagRule(itemId)}
                    onUpdate={updateTagRule(itemId)}
                    preview={preview}
                    userCanEdit={userCanEdit}
                    setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
                  />
                )}
              </div>
            );
          }}
        />
      )}
    </Table>
  );
};

const TagForm = ({
  tagId,
  tag,
  scope,
  offerId,
  preview,
  userIsAdmin,
  userIsOfferEditor,
  teams,
  userTeam
}) => {
  const { globalVariables: globalVariablesFetch, isLoading } = useFetchQuery({
    query: QGlobalVariablesQuery,
    args: {
      scope,
      offerId,
      checkUsage: false
    },
    dataProp: 'globalVariables',
    defaultData: []
  });

  const {
    isEdit,
    name,
    itemType,
    color,
    rules,
    setState,
    onSave,
    isValid,
    onItemUp,
    onItemDown,
    itemsIds,
    getRulesAreValid,
    allRulesAreValid,
    getConditionsAreValid,
    addTagRule,
    removeTagRule,
    updateTagRule,
    tagExist,
    isOpenCreateGlobalVariableModal,
    setIsOpenCreateGlobalVariableModal,
    setGlobalVariableCreated,
    globalVariables,
    removeTag,
    loading
  } = useTag({
    tagId, tag, scope, offerId, globalVariablesFetch, isLoading
  });

  const { offer, isLoading: isLoadingOffer } = useFetchQuery({
    query: QOfferQuery,
    args: { offerId },
    dataProp: 'offer',
    defaultData: {},
    skip: !(isEdit && offerId)
  });

  const isDefaultTag = propOr(false, 'isDefault', tag);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const tagEditable = not(preview) && not(isDefaultTag);
  const title = cond([
    [() => tagEditable && tag, always('Modifier le tag')],
    [() => isTrue(preview), always('Prévisualiser le tag')],
    [T, always('Créer un tag')]
  ])(tag);

  const { colors, isLoading: isLoadingColors } = useColors();
  const favoritesColors = [defaultColors, colors];
  const tileContainUnexpectedCharacter = test(/"|'/, name);

  const isTagUsed = compose(
    not,
    isEmpty,
    prop('itemsIds')
  )(tag);

  if (isLoading || isLoadingColors || isLoadingOffer) return <Loading/>;

  return (
    <>
      {isNotNil(isOpenCreateGlobalVariableModal) && (
        <GlobalVariableForm
          isOpenCreateGlobalVariableModal={isOpenCreateGlobalVariableModal}
          setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
          scope={SCOPE_OFFER}
          offerId={offerId}
          modalMode
          setGlobalVariableCreated={setGlobalVariableCreated}
        />
      )}
      <Title
        title={title}
        backTo={offerId ? `/offre/${offerId}/edition` : '/equipe/tags'}
      />
      <Section title="nom du tag">
        <div className="flex gap-4">
          <div className="w-full">
            <Input
              className="h-50px"
              value={name}
              placeholder="Nom du tag"
              onChange={(e) => setState(assoc('name', e.target.value))}
              size="middle"
              disabled={not(tagEditable)}
              status={tileContainUnexpectedCharacter && 'error'}
            />
            <p className="text-flexi-red pt-1 h-6">{tileContainUnexpectedCharacter ? 'Les guillemets ne sont pas autorisés' : ''}</p>
          </div>
        </div>

        <div className="mt-8 ml-1">
          <label className="mb-2 block font-bold">Couleur</label>
          {tagExist ? (
            <div className="relative w-72">
              {showColorPicker && (
                <Components.ColorPickerBody
                  color={color || '#000'}
                  setColor={(v) => setState(assoc('color', v))}
                  handleColorChange={(v) => setState(assoc('color', v))}
                  setShowOptions={setShowColorPicker}
                  favoritesColors={favoritesColors}
                  isDraggable
                />
              )}
              {tagEditable ? (
                <div
                  className="flex items-center gap-2 cursor-pointer h-12 text-flexi-primary-color"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                >
                  <ColorBlock color={color || '#000'} />
                </div>
              ) : <ColorBlock color={color || '#000'} />}
            </div>
          ) : (
            <div className="flex items-start">
              <Components.ColorPickerBody
                color={color || '#000'}
                setColor={(v) => setState(assoc('color', v))}
                handleColorChange={(v) => setState(assoc('color', v))}
                favoritesColors={[]}
              />
            </div>
          )}
        </div>
        {isEdit && (
          <TagItems
            preview={preview}
            itemType={itemType}
            itemsIds={itemsIds}
            onItemUp={onItemUp}
            onItemDown={onItemDown}
            rules={offerId && (rules || [])}
            getRulesAreValid={getRulesAreValid}
            getConditionsAreValid={getConditionsAreValid}
            variables={globalVariables}
            addTagRule={addTagRule}
            removeTagRule={removeTagRule}
            updateTagRule={updateTagRule}
            userCanEdit={userIsAdmin || userIsOfferEditor}
            setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
            tag={tag}
          />
        )}
      </Section>

      <div className="flex flex-row justify-center items-center gap-x-3 mb-3">
        {!preview && isEdit && (
          <Button
            onClick={removeTag}
            disabled={isTagUsed}
            loading={propOr(false, `delete-tag-${tagId}`, loading)}
            type="danger"
            size="large"
            icon={<DeleteOutlined/>}
            className="btn-remove-tag"
          >
            Supprimer
          </Button>
        )}
        {!preview && (
          <Button
            onClick={onSave}
            disabled={!isValid || !allRulesAreValid || tileContainUnexpectedCharacter}
            loading={propOr(false, `${isEdit ? 'update' : 'create'}-tag-${isEdit ? tagId : offerId}`, loading)}
            type="primary"
            size="large"
            icon={<ThunderboltOutlined />}
          >
            {isEdit ? 'Mettre à jour le tag' : 'Créer le tag'}
          </Button>
        )}
      </div>

      {isEdit && offerId && (
        <TagCurlSection
          bundleTag={name}
          offerId={offerId}
          teams={teams}
          userTeam={userTeam}
          offer={offer}
        />
      )}
    </>
  );
};

export default withUser(TagForm);
